<template lang="pug">
div
  div.d-flex.justify-center.mt-16(v-if="Object.keys(patient).length === 0")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
  v-row(v-else)
    v-col(cols="4" style="min-width: 480px;")
      left-panel-registration(:patient="patient" @goback="$emit('goback')")
    v-col.grow
      v-alert.mb-3(v-if="hasReservationsOrPreorders" dense style="background-color: #FFF7D9;")
        v-row(no-gutters align="center")
          v-col
            .d-flex.align-center
              yz-icon.mr-2(color="danger500") mdi-alert-circle
              div
                div.yz-heading-9 {{ reservationPreorderBannerText }}
                div.yz-regular-body-sm Review and add them to today's treatment plan during registration.
          v-col.shrink
            yz-btn(label="View" size="md" outlined, background-color="white" @click="showReservationPreorderDialog = true")
      v-form(ref="form")
        v-card.round-xl.pa-4(outlined)
          v-row.mt-4(no-gutters style="gap: 20px;")
            v-col
              div.text-md.font-semibold.neutral700--text.mb-5 Visit details
              template(v-if="!registration.pid")
                div.text-sm.font-medium.neutral700--text Visit purpose
                yz-radio-group.mt-2(v-model="purpose" direction="horizontal")
                  yz-radio(value="consultation" label="Consultation")
                  yz-radio(value="otc" label="OTC")

              div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('doctor') }} (Optional)
              doctor-list-dropdown.mt-2(v-model="registration.provider" :items="providers" :placeholder="$t('select_doctor')" dense)
              
              div.text-sm.font-medium.neutral700--text.mt-4 Visit notes
              yz-textarea.text-sm.mt-2.round-xl(v-model="registration.remarks" placeholder="Eg: Shortness of breath" outlined rows="3" background-color="neutral100" hide-details="auto")
            yz-divider.yz-neutral-300(vertical)
            v-col
              .yz-heading-8.mb-5 {{ $t('panel.billing_details') }}

              .yz-medium-body-sm.mt-4 {{ $t('panel.who_is_this_person') }}
              yz-radio-group.mt-2(v-model="billingDetailDependency" direction="horizontal" spacing="80px" @input="checkBillingDependency(billingDetailDependency)")
                yz-radio(value="self" :label="$t('registration_page.registration_form.self')")
                yz-radio(value="dependent" :label="$t('registration_page.registration_form.dependent')")
              yz-card.px-4.py-3.mt-2(v-if="billingAddress && Object.keys(billingAddress).length > 0")
                v-row(no-gutters)
                  v-col
                    .yz-medium-body-xs.text-neutral-700 {{ billingAddress.name }} - {{ billingAddress.phone }}
                    template(v-if="getRelationship(billingAddress)")
                      v-chip.px-1(x-small outlined color="blue500") {{ getRelationship(billingAddress) }}
                  v-col.shrink
                    yz-btn(size="sm" label="Change" outlined @click="showEditBillingInfoDialog = true")


              div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('payment_method') }}
              panel-list-dropdown.mt-2(v-model="payment.panel" :items="panels" :placeholder="$t('select_payment_method')" :null-value-text="$t('self_pay')" dense, @input="onInputPayment")
              yz-card.pa-4.mt-3(v-if="payment.panel" color="neutral-200")
                template(v-if="isPanelRemarkType")
                  .yz-medium-body-xs.text-neutral-500 {{ $t('panel.remarks') }}
                  .yz-regular-body-sm {{ panelRemarkText }}

                template(v-else)
                  v-row(no-gutters align="center")
                    v-col
                      .yz-medium-body-xs.text-neutral-500 {{ $t('panel.verification_link') }}
                    v-col.shrink
                      yz-btn(size="sm" :label="$t('panel.open_link')" :disabled="!getPanelLink(payment.panel)" @click="openLink(getPanelLink(payment.panel))")

                yz-divider.my-4

                v-row(no-gutters)
                  v-col
                    .yz-medium-body-sm {{ $t('panel.staff_id') }}
                  v-col.shrink
                    yz-switch(v-model="showStaffId" size="sm")
                  v-col(v-if="showStaffId" cols="12")
                    yz-text-field.mt-1(v-model="staffId" :placeholder="$t('panel.enter_staff_id')")

                v-row.mt-3(no-gutters)
                  v-col
                    .yz-medium-body-sm {{ $t('panel.chit_number') }}
                  v-col.shrink
                    yz-switch(v-model="showChitNumber" size="sm")
                  v-col(v-if="showChitNumber" cols="12")
                    yz-text-field.mt-1(v-model="chitNumber" :placeholder="$t('panel.enter_chit_number')")

                .yz-medium-body-sm.mt-3 Panel note
                yz-textarea.mt-1(v-model="payment.note" placeholder="Enter panel note" background-color="neutral100" outlined rows="3" hide-details="auto")

        v-row.mt-3(no-gutters style="gap: 12px;")
          v-col
            v-card.round-xl.pa-4(outlined)
              v-card.round-lg(outlined height="65")
                div.d-flex.align-center.fill-height.neutral200.px-3
                  v-checkbox.my-0.py-0(v-model="registration.urgent" on-icon="$yzi-check-square-solid" off-icon="$yzi-checkbox-uncheck" dense hide-details="auto")
                    template(#label)
                      div
                        div.text-sm.font-semibold.neutral700--text Mark this visit as urgent
                        div.text-sm.font-normal.neutral700--text Indicate this visit needs immediate attention.
          v-col
            v-card.round-xl.pa-4(outlined)
              v-card.round-lg(outlined height="65")
                div.d-flex.align-center.fill-height.neutral200.px-3
                  v-row(no-gutters)
                    v-col.grow
                      div.text-sm.font-semibold.neutral700--text Label
                      div.text-sm.font-normal.neutral700--text Print details for investigation purposes.
                    v-col.shrink
                      y-button(label="Print" size="xxs" height="24" unset-min-width button-class="px-2" :loading="isLoadingPrint" @click="printPatientLabel")
        v-card.round-xl.pa-4.mt-3(v-if="purpose === 'consultation'" outlined)
          div.text-md.font-semibold.neutral700--text Vital signs on this visit
          registration-vital-sign.mt-2(ref="vitalsign" :patient="patient" :vital-signs.sync="vitalSigns" :pid="leadPid")
  
  .dialogs
    edit-billing-info-dialog(v-if="showEditBillingInfoDialog" :open.sync="showEditBillingInfoDialog" :parentBillingInfo="billingAddress" :parentPatient="patient" :saveToApi="false" @updated="handleBillingInfoUpdated" @cancel="showEditBillingInfoDialog = false")
    view-reservations-preorders-dialog(v-if="showReservationPreorderDialog" :open.sync="showReservationPreorderDialog" :reservations="reservations" :preorders="preorders" @confirm="onConfirmReservationSelection")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import LeftPanelRegistration from '@/components/clinic/registration/LeftPanelRegistration.vue'
import RegistrationVitalSign from '@/components/clinic/registration/RegistrationVitalSign.vue'
import PanelListDropdown from '@/components/clinic/custom/PanelListDropdown.vue'
import DoctorListDropdown from '@/components/clinic/custom/DoctorListDropdown.vue'
import EditBillingInfoDialog from '@/components/clinic/dialogs/EditBillingInfoDialog.vue'
import ViewReservationsPreordersDialog from '@/components/clinic/registration/ViewReservationsPreordersDialog.vue'

export default {
  components: { LeftPanelRegistration, RegistrationVitalSign, PanelListDropdown, DoctorListDropdown, EditBillingInfoDialog, ViewReservationsPreordersDialog },
  props: {
    patientData: {
      type: Object,
      default: (() => {})
    },
    purposeData: {
      type: String,
      default: 'consultation',
      validator(value) {
        return ['consultation', 'otc'].includes(value)
      }
    },
    selectedRegistration: {
      default: null
    },
    appointment: {
      type: Object,
      default: null
    },
    needMarkAsArrived: {
      type: Boolean,
      default: false
    },
    reservationFulfillItem: {
      type: Object,
      default: null
    },
  },
  data () {
    return {
      isLoadingPrint: false,
      showStaffId: false,
      showChitNumber: false,
      billingDetailDependency: 'self',
      showEditBillingInfoDialog: false,
      staffId: '',
      chitNumber: '',
      providers: [],
      panels: [{ id: null, name: 'Self pay', verification_link: null}],
      vitalSigns: [],
      patient: {},
      registration: {},
      payment: {
        panel: null,
        type: 'panel',
        note: ''
      },
      billingAddress: null,
      reservations: [],
      preorders: [],
      selectedReservationItems: [],
      showReservationPreorderDialog: false
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic',
    }),
    leadPid () {
      return this.selectedRegistration?.pid || null
    },
    purpose: {
      get () {
        return this.purposeData
      },
      set (newValue) {
        this.$emit('update:purposeData', newValue)
      }
    },
    selectedPanel () {
      const found = this.panels.find(panel => panel.id === this.payment.panel)
      if (found) return found
      return null
    },
    isPanelRemarkType () {
      return this.selectedPanel?.display_remarks ?? false
    },
    panelRemarkText () {
      return this.selectedPanel?.remarks && this.selectedPanel.remarks !== '' ? this.selectedPanel.remarks : '-'
    },
    hasReservationsOrPreorders () {
      return this.reservations.length > 0 || this.preorders.length > 0
    },
    reservationPreorderBannerText () {
      const parts = []
      if (this.reservations.length > 0) {
        parts.push(`${this.reservations.length} reservation${this.reservations.length > 1 ? 's' : ''}`)
      }
      if (this.preorders.length > 0) {
        parts.push(`${this.preorders.length} pre-order${this.preorders.length > 1 ? 's' : ''}`)
      }
      return `${parts.join(' & ')} available for this patient.`
    },
  },
  mounted () {
    this.setRegistrationData()
    this.fetchProviders()
    this.fetchPanel()
    if (this.patientData?.id) {
      this.fetchReservationsAndPreorders()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setRegistrationData () {
      this.patient = JSON.parse(JSON.stringify(this.patientData))
      if (this.patientData?.last_visit?.default_payment?.type === 'panel' && this.patient.last_visit.default_payment.panel) {
        this.payment.panel = this.patient.last_visit.default_payment.panel.id
        // Initialize staff ID and note from last visit data
        this.staffId = this.patient.last_visit.default_payment?.staff_id || ''
        this.payment.note = this.patient.last_visit.default_payment?.note || ''
        this.showStaffId = !!this.staffId
      }

      if (this.leadPid) {
        this.registration = {
          pid: this.selectedRegistration.pid,
          urgent: this.selectedRegistration.urgent,
          remarks: this.selectedRegistration.remarks,
          provider: this.selectedRegistration?.provider?.id ?? null
        }
        this.payment = {
          panel: this.selectedRegistration?.default_payment?.panel?.id ?? null,
          type: this.selectedRegistration.default_payment.type,
          note: this.selectedRegistration.default_payment.note
        }
        // Initialize staff ID and chit number from existing registration data
        if (this.selectedRegistration?.default_payment?.staff_id) {
          this.staffId = this.selectedRegistration.default_payment.staff_id
          this.showStaffId = true
        }
        if (this.selectedRegistration?.default_payment?.chit_number) {
          this.chitNumber = this.selectedRegistration.default_payment.chit_number
          this.showChitNumber = true
        }
        if (this.selectedRegistration.code === 3){
          this.purpose = 'otc'
        }
        if (this.selectedRegistration.billing_address && Object.keys(this.selectedRegistration.billing_address).length > 0) {
          this.billingDetailDependency = 'dependent'
          this.billingAddress = { ...this.selectedRegistration.billing_address }
        }
        this.$nextTick(() => {
          if (this.$refs.vitalsign) {
            this.$refs.vitalsign.fetchVitalSigns()
          }
        })
      }
    },
    fetchProviders () {
      this.$axios.get(`/clinics/${this.company.slug}/providers?page_size=-1&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.providers = resp.data.results
          }
        })
    },
    fetchPanel () {
      let params = {
        company: this.company.id,
        enabled: true,
        channel: 'panel',
        page_size: -1
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            let data = resp.data.results
            data.forEach(o => { this.panels.push(o) })
          }
        })
    },
    async saveData () {
      try {
        this.$emit('loading', true)
        let endpoint, method, activity, resp, data
        if (this.leadPid) {
          // PATCH: Include billing address in the request
          data = this.prepareRegistrationData(true)
          endpoint = `/clinics/${this.company.slug}/registrations/${this.leadPid}`
          method = 'PATCH'
          activity = 'Edit registration'
          resp = await this.$axios.patch(endpoint, data)
        } else {
          // POST: Exclude billing address from initial request
          data = this.prepareRegistrationData(false)
          endpoint = `/clinics/${this.company.slug}/registrations`
          method = 'POST'
          activity = 'Register existing patient'
          resp = await this.$axios.post(endpoint, data)
        }
        await this.handleRegistrationSuccess(resp, endpoint, data, method, activity)
      } catch (err) {
        let message = this.$t('error_message.unable_to_complete_request')
        if (err.response?.data) {
          let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
          if (data) { message = data }
        }
        this.showMessage(['error', message])
      } finally {
        this.$emit('loading', false)
      }
    },
    prepareRegistrationData(includeBillingAddress = true) {
      const data = {
        ...this.registration,
        patient: this.patient.id
      }
      if (!this.leadPid && this.appointment) {
        data.code = 2
        data.appointment = this.appointment.id
      }
      if (this.payment.panel) {
        data.default_payment = {
          ...this.payment,
          type: 'panel',
        }
        if (this.showStaffId) {
          data.default_payment.staff_id = this.staffId || null
        }
        if (this.showChitNumber) {
          data.default_payment.chit_number = this.chitNumber || null
        }
        data.default_payment.self = this.billingDetailDependency === 'self'
      } else {
        data.default_payment = { type: 'self-pay', note: '' }
      }
      if (!this.leadPid && this.purpose === 'otc') {
        data.code = 3
        data.status = 'checkout'
      }
      if (includeBillingAddress && this.billingDetailDependency === 'dependent') {
        data.billing_address = { ...this.billingAddress }
      }
      return data
    },
    async updateBillingAddress(pid) {
      const endpoint = `/clinics/${this.company.slug}/registrations/${pid}`
      const data = {
        billing_address: { ...this.billingAddress }
      }
      try {
        const resp = await this.$axios.patch(endpoint, data)
        this.updateActivityLog(endpoint, data, resp, 'PATCH', pid, 'registration', 'Update billing address')
      } catch (err) {
        let message = this.$t('error_message.unable_to_complete_request')
        if (err.response?.data) {
          let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
          if (data) { message = data }
        }
        this.showMessage(['error', message])
        throw err
      }
    },
    async handleRegistrationSuccess(resp, endpoint, data, method, activity) {
      await this.handleMarkAsArrived()
      await this.handleVitalSigns(resp.data.pid)

      // For POST operations, update billing address separately if needed (this is due to limitation from API to make address optional on creation)
      if (!this.leadPid && this.billingDetailDependency === 'dependent') {
        await this.updateBillingAddress(resp.data.pid)
      }

      // Auto-add reservation fulfill item from parent (e.g. reservation detail page)
      if (this.reservationFulfillItem) {
        this.selectedReservationItems.push(this.reservationFulfillItem)
      }

      // Fulfill selected reservations/pre-orders
      let failedFulfillments = []
      if (this.selectedReservationItems.length > 0) {
        failedFulfillments = await this.fulfillSelectedReservations(resp.data.pid)
        this.$emit('fulfilled')
      }

      if (failedFulfillments.length > 0) {
        const failedList = failedFulfillments.map(i => `${i.type} #${i.id}`).join(', ')
        const baseMessage = this.leadPid ? 'Registration edited' : 'Patient registered'
        this.showMessage(['error', `${baseMessage}, but failed to fulfill: ${failedList}`])
      } else {
        const successMessage = this.leadPid ? 'Registration edited successfully' : 'Patient registered successfully'
        this.showMessage(['success', successMessage])
      }
      this.updateActivityLog(endpoint, data, resp, method, resp.data.id, 'registration', activity)
      this.$emit('update')

      if (this.purpose === 'otc') {
        this.$router.push({ name: 'ClinicCheckout', params: { pid: resp.data.pid, origin: 'ClinicRegistration' }})
      }
    },
    async handleVitalSigns (pid) {
      if (this.purpose === 'otc') {
        return
      }
      if (this.vitalSigns.length > 0) {
        const vitalsigns = this.vitalSigns
        try {
          if (!this.leadPid) await this.$axios.get(`/clinics/${this.company.slug}/consultations/${pid}`)
          for (const vitalsign of vitalsigns) {
            const endpoint = `/clinics/${this.company.slug}/consultations/${pid}/vital_signs`
            if (vitalsign.id) {
              await this.$axios.patch(`${endpoint}/${vitalsign.id}`, vitalsign)
            } else {
              await this.$axios.post(endpoint, vitalsign)
            }
          }
        } catch (err) {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        }
      }
    },
    async handleMarkAsArrived () {
      if (this.needMarkAsArrived) {
        try {
          const endpoint = `/clinics/${this.company.slug}/appointments/${this.appointment.id}`
          const data = { status: 'received' }
          const resp = await this.$axios.patch(endpoint, data)
          const ref = 'appointment'
          const activity = 'Update appointment as completed'
          const id = this.appointment.id
          this.updateActivityLog(endpoint, data, resp, 'PATCH', id, ref, activity)
        } catch (err) {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        }
      }
    },
    getPanelLink (id) {
      if (id && this.panels.find(f => f.id === id)) {
        return this.panels.find(f => f.id === id).verification_link
      }
      return ''
    },
    openLink(link) {
      window.open(link, '_blank')
    },
    async printPatientLabel () {
      if (!this.leadPid) {
        this.showMessage(['error', 'Please register the patient first'])
        return
      }
      this.isLoadingPrint = true
      // Get key first
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoadingLabels = false
        return
      }
      // Open download url
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/clinics/${this.company.slug}/registrations/${this.leadPid}/preview_patient_label?key=${key}`
      window.open(url, '_blank')
      this.isLoadingPrint = false
    },
    updateActivityLog (endpoint, payload, response, method, id, ref, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: ref,
        activity: activity,
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    async fetchReservationsAndPreorders () {
      try {
        const [reservationsResp, preordersResp] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params: { patient: this.patientData.id, status: 'active', page_size: -1 } }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params: { patient: this.patientData.id, status: 'active', page_size: -1 } })
        ])
        if (reservationsResp.data?.results) {
          this.reservations = reservationsResp.data.results.filter(r => r.products.length > 0)
        }
        if (preordersResp.data?.results) {
          this.preorders = preordersResp.data.results.filter(r => r.products.length > 0)
        }
        if (this.reservationFulfillItem) {
          if (this.reservationFulfillItem.type === 'reservation') {
            this.reservations = this.reservations.filter(r => r.id !== this.reservationFulfillItem.id)
          } else if (this.reservationFulfillItem.type === 'preorder') {
            this.preorders = this.preorders.filter(p => p.id !== this.reservationFulfillItem.id)
          }
        }
      } catch {
        // silently ignore
      }
    },
    onConfirmReservationSelection (items) {
      this.selectedReservationItems = items
      const selectedReservationIds = items.filter(i => i.type === 'reservation').map(i => i.id)
      const selectedPreorderIds = items.filter(i => i.type === 'preorder').map(i => i.id)
      this.reservations = this.reservations.filter(r => !selectedReservationIds.includes(r.id))
      this.preorders = this.preorders.filter(p => !selectedPreorderIds.includes(p.id))
    },
    async fulfillSelectedReservations (registrationPid) {
      const failed = []
      for (const item of this.selectedReservationItems) {
        try {
          if (item.type === 'reservation') {
            await this.$axios.post(`/clinics/${this.company.slug}/reservations/${item.id}/fulfill/`, { registration_pid: registrationPid })
          } else if (item.type === 'preorder') {
            await this.$axios.post(`/clinics/${this.company.slug}/preorders/${item.id}/fulfill/`, { registration_pid: registrationPid })
          }
        } catch (err) {
          console.error(err)
          failed.push(item)
        }
      }
      return failed
    },
    onInputPayment() {
      // Clear existing staff ID and note
      this.staffId = ''
      this.payment.note = ''
      this.showStaffId = false

      // Selected a current default payment panel
      if (this.selectedRegistration?.default_payment?.panel?.id === this.payment.panel) {
        if (this.selectedRegistration.default_payment.staff_id) {
          this.staffId = this.selectedRegistration.default_payment.staff_id
          this.showStaffId = true
        }
        if (this.selectedRegistration.default_payment.chit_number) {
          this.chitNumber = this.selectedRegistration.default_payment.chit_number
          this.showChitNumber = true
        }
        if (this.selectedRegistration.default_payment.note) {
          this.payment.note = this.selectedRegistration.default_payment.note
        }
        return
      }

      // Populate from patient panels data if there's a matching panel
      if (this.patient.panels && this.patient.panels.length > 0 && this.payment.panel) {
        const matchingPanel = this.patient.panels.find(p => p.panel.payment_method === this.payment.panel)
        if (matchingPanel) {
          if (matchingPanel.staff_id) {
            this.staffId = matchingPanel.staff_id
            this.showStaffId = true
          }
          if (matchingPanel.last_visit_note) {
            this.payment.note = matchingPanel.last_visit_note
          }
        }
      }
    },
    getRelationship (data) {
      if (data.extra_data && data.extra_data.relationship && data.extra_data.relationship.name) {
        return data.extra_data.relationship.name
      }
      return null
    },
    checkBillingDependency(data) {
      if (data === 'dependent') {
        this.showEditBillingInfoDialog = true
      } else {
        this.billingAddress = null
      }
    },
    handleBillingInfoUpdated(billingInfo) {
      this.billingAddress = { ...billingInfo }
      this.showEditBillingInfoDialog = false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
</style>